/**
 * The upload tray (Phase 91, 91-09; UI-SPEC §5): bottom left, shown only
 * while a file is still moving after Save and next opened the next product.
 * One row per file: its name, then the progress, "Paused, offline", or the
 * failure with Try again (a save waiting on that file goes once it is stored)
 * or Dismiss (the file is let go; a save waiting on it fails with Open it).
 * "Keep this tab open" sits under the rows while anything still uploads.
 */
import { useSyncExternalStore } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertTriangle, Loader2, RotateCw, WifiOff, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { StudioUploadStore } from '../lib/studio-upload-store';

const megabytes = (bytes: number) => (bytes / (1024 * 1024)).toFixed(1);

export function UploadTray({ uploads }: { uploads: StudioUploadStore }) {
  const { t } = useTranslation('uploadStudio');
  const rows = useSyncExternalStore(uploads.subscribe, uploads.moving, uploads.moving);
  if (rows.length === 0) return null;
  const stillMoving = rows.some((r) => r.state.phase === 'uploading' || r.state.phase === 'paused');

  return (
    <aside
      aria-label={t('upload.trayLabel')}
      // D1: clear of the sidebar (16 rem open, 3 rem as icons). The tray lives
      // above the app shell, outside the sidebar's context, so its state is read
      // from the sidebar's own data attributes.
      className="fixed bottom-4 left-4 z-40 w-72 transition-[left] duration-200 ease-linear motion-reduce:transition-none md:[body:has(.peer[data-side=left][data-state=expanded])_&]:left-[calc(16rem+1rem)] md:[body:has(.peer[data-side=left][data-collapsible=icon])_&]:left-[calc(3rem+1rem)] max-w-[calc(100vw-2rem)] space-y-2 rounded-lg border bg-background p-3 shadow-lg"
      data-testid="upload-tray"
    >
      <ul className="space-y-2">
        {rows.map(({ key, state }) => {
          const name = state.file?.fileName ?? '';
          const size = state.file?.byteSize ?? null;
          return (
            <li key={key} className="space-y-1">
              <p className="truncate text-xs font-medium text-foreground" title={name}>
                {name}
              </p>
              {state.phase === 'uploading' && (
                <p className="flex items-center gap-1.5 text-xs tabular-nums text-muted-foreground" role="status">
                  <Loader2 className="h-3.5 w-3.5 animate-spin motion-reduce:animate-none" aria-hidden="true" />
                  {size === null
                    ? `${Math.round(state.progress * 100)}%`
                    : t('upload.progress', {
                        pct: Math.round(state.progress * 100),
                        done: megabytes(size * state.progress),
                        total: megabytes(size),
                      })}
                </p>
              )}
              {state.phase === 'paused' && (
                <p className="flex items-center gap-1.5 text-xs text-amber-800" role="status">
                  <WifiOff className="h-3.5 w-3.5" aria-hidden="true" />
                  {t('upload.paused')}
                </p>
              )}
              {state.phase === 'failed' && (
                <div className="flex items-center justify-between gap-2" role="alert">
                  <p className="flex min-w-0 items-center gap-1.5 text-xs text-destructive">
                    <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                    <span>{t('upload.didntUpload')}</span>
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    className="h-8 shrink-0 gap-1 px-2 text-xs"
                    onClick={() => void uploads.retry(key)}
                  >
                    <RotateCw className="h-3.5 w-3.5" aria-hidden="true" />
                    {t('upload.retry')}
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    className="h-8 w-8 shrink-0 p-0"
                    aria-label={t('upload.dismiss', { file: name })}
                    title={t('upload.dismiss', { file: name })}
                    onClick={() => uploads.dismiss(key)}
                  >
                    <X className="h-4 w-4" aria-hidden="true" />
                  </Button>
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {stillMoving && <p className="text-[11px] text-muted-foreground">{t('upload.keepOpen')}</p>}
    </aside>
  );
}
